// src/components/HomePage.js
import "./Homepage.css";
import {
  DatasetGrid,
  EntryGrid,
  OwnerTemplateGrid,
  PermissionTemplateGrid,
} from "./TileComponents";

import { useSelector } from "react-redux";
import { allDatasets, allTemplates, userData, userTemplates } from "../Common/Store/CommonSignals";
import { selectUserLastEntries } from "../Entry/EntrySlice";
import { selectedHamburgerIndex } from "../Navigation/HamburgerMenu/HamburgerMenuSignals";
import { userBelongsToUserGroup, userHasAccessToResource } from "../Utilities/AuthorizationUtil";

const UserPermissionText = (props) => {
  //set permission based on user email and shared permission
  if (props.owner.toLowerCase() === props.email.toLowerCase()) {
    return "Owner";
  } else if (
    props.coOwners.some((co) => {
      // Check individual co-owners
      if (!co.isUserGroup && co.user.toLowerCase() === props.email.toLowerCase()) {
        return true;
      }
      // Check if user belongs to a co-owner group
      if (co.isUserGroup && co.userGroupId) {
        return userBelongsToUserGroup(props.email, co.userGroupId);
      }
      return false;
    })
  ) {
    return "Co-Owner";
  } else if (
    props.sharedPermission.some((sp) => {
      // Check individual permissions
      if (
        !sp.isUserGroup &&
        sp.entity.toLowerCase() === props.email.toLowerCase() &&
        sp.permission === "Modify"
      ) {
        return true;
      }
      // Check if user belongs to a group with Modify permission
      if (sp.isUserGroup && sp.userGroupId && sp.permission === "Modify") {
        return userBelongsToUserGroup(props.email, sp.userGroupId);
      }
      return false;
    })
  ) {
    return "Modify";
  } else if (
    props.sharedPermission.some((sp) => {
      // Check individual permissions
      if (
        !sp.isUserGroup &&
        sp.entity.toLowerCase() === props.email.toLowerCase() &&
        sp.permission === "View"
      ) {
        return true;
      }
      // Check if user belongs to a group with View permission
      if (sp.isUserGroup && sp.userGroupId && sp.permission === "View") {
        return userBelongsToUserGroup(props.email, sp.userGroupId);
      }
      return false;
    })
  ) {
    return "View Only";
  } else if (
    props.sharedPermission.some((sp) => {
      // Check individual permissions
      if (
        !sp.isUserGroup &&
        sp.entity.toLowerCase() === props.email.toLowerCase() &&
        sp.permission === "MakeEntry"
      ) {
        return true;
      }
      // Check if user belongs to a group with MakeEntry permission
      if (sp.isUserGroup && sp.userGroupId && sp.permission === "MakeEntry") {
        return userBelongsToUserGroup(props.email, sp.userGroupId);
      }
      return false;
    })
  ) {
    return "Make Entry";
  } else if (
    props.sharedPermission.some((sp) => {
      // Check individual permissions
      if (
        !sp.isUserGroup &&
        sp.entity.toLowerCase() === props.email.toLowerCase() &&
        sp.permission === "ViewAuthorOnly"
      ) {
        return true;
      }
      // Check if user belongs to a group with ViewAuthorOnly permission
      if (sp.isUserGroup && sp.userGroupId && sp.permission === "ViewAuthorOnly") {
        return userBelongsToUserGroup(props.email, sp.userGroupId);
      }
      return false;
    })
  ) {
    return "Make Entry (View Author Only)";
  } else if (
    props.sharedPermission.some((sp) => {
      // Check individual permissions
      if (
        !sp.isUserGroup &&
        sp.entity.toLowerCase() === props.email.toLowerCase() &&
        sp.permission === "MakeEntry (Modify All)"
      ) {
        return true;
      }
      // Check if user belongs to a group with MakeEntry (Modify All) permission
      if (sp.isUserGroup && sp.userGroupId && sp.permission === "MakeEntry (Modify All)") {
        return userBelongsToUserGroup(props.email, sp.userGroupId);
      }
      return false;
    })
  ) {
    return "Make Entry (Modify All)";
  }
};

const HomePage = () => {
  selectedHamburgerIndex.value = 1;
  const userEmail = userData.value?.userEmail;
  const templates = allTemplates.value;
  const datasets = allDatasets.value;
  const lastEntries = useSelector(selectUserLastEntries);

  const accessTemplates = userTemplates.value;

  const userDatasets = datasets.filter((dataset) => {
    return userHasAccessToResource(dataset, userEmail);
  });

  const templateSearch = accessTemplates.map((template) => ({
    ...template,
    label: template.templateName,
    permission: UserPermissionText({ ...template, email: userEmail }),
    type: "Template",
  }));

  const datasetSearch = userDatasets.map((dataset) => ({
    ...dataset,
    label: dataset.datasetName,
    permission: UserPermissionText({ ...dataset, email: userEmail }),
    type: "Dataset",
  }));

  const ownerTemplates = templateSearch.filter((template) => {
    return (
      template.owner.toLowerCase() === userEmail.toLowerCase() ||
      template.coOwners.some((co) => {
        // Check individual co-owners
        if (!co.isUserGroup && co.user.toLowerCase() === userEmail.toLowerCase()) {
          return true;
        }
        // Check if user belongs to a co-owner group
        if (co.isUserGroup && co.userGroupId) {
          return userHasAccessToResource({ coOwners: [co] }, userEmail);
        }
        return false;
      })
    );
  });

  const permissionTemplates = templateSearch.filter((template) => {
    return template.sharedPermission.some((sp) => {
      // Check individual permissions
      if (!sp.isUserGroup && sp.entity.toLowerCase() === userEmail.toLowerCase()) {
        return true;
      }
      // Check if user belongs to a group with permissions
      if (sp.isUserGroup && sp.userGroupId) {
        return userBelongsToUserGroup(userEmail, sp.userGroupId);
      }
      return false;
    });
  });

  return (
    <div className="main-content">
      <div className="templateBar">
        <PermissionTemplateGrid tables={permissionTemplates} />
        <OwnerTemplateGrid tables={ownerTemplates} />
      </div>
      <div className="videoBar">
        <DatasetGrid tables={datasetSearch} />
        <EntryGrid
          tables={lastEntries}
          templates={templates}
        />
      </div>
    </div>
  );
};

export default HomePage;
